import { useState } from 'react';
import { closestCorners, rectIntersection } from '@dnd-kit/core';
import { canAddComponentToScreen, getAddBlockerMessage } from '../utils/componentUtils';
import { getFooterIndex } from '../utils/footerManager';

export const useDragAndDrop = (screens, activeScreen, addComponent, reorderComponents, onDropBlocked, addComponentToBranch, reorderBranchComponents) => {
  const [activeId, setActiveId] = useState(null);
  const [hoveredComponent, setHoveredComponent] = useState(null);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (active?.id.startsWith('palette-') && over?.data.current?.type === 'sortable-component') {
      setHoveredComponent(over.data.current.component.id);
    } else {
      setHoveredComponent(null);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over) {
      setActiveId(null);
      setHoveredComponent(null);
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    const isActiveFromPalette = activeId.startsWith('palette-');
    const overType = over.data.current?.type;
    const isOverCanvas = overType === 'canvas';
    const isOverDropZone = overType === 'drop-zone';
    const isOverSortableComponent = overType === 'sortable-component';
    const isOverBranchDropZone = overType === 'branch-drop-zone';
    const isOverBranchZone = overType === 'branch-zone';
    const isOverBranchSortable = overType === 'branch-sortable';
    const isActiveBranchSortable = active.data.current?.type === 'branch-sortable';

    const currentScreen = screens.find(screen => screen.id === activeScreen);

    // Palette drop into a branch zone
    if (isActiveFromPalette && (isOverBranchDropZone || isOverBranchZone)) {
      const componentType = active.data.current.componentType;
      const defaultProperties = active.data.current.defaultProperties;
      const { parentId, branchKey, index } = over.data.current;
      addComponentToBranch(parentId, branchKey, componentType, defaultProperties, isOverBranchDropZone ? index : undefined);
      setActiveId(null);
      setHoveredComponent(null);
      return;
    }

    // Branch item reorder (drag within same branch)
    if (isActiveBranchSortable && activeId !== overId) {
      const { parentId, branchKey } = active.data.current;
      const parentComp = currentScreen?.components.find(c => c.id === parentId);
      let branch;
      if (branchKey.startsWith('case:')) {
        const caseId = branchKey.slice(5);
        branch = parentComp?.properties?.cases?.find(c => c.id === caseId)?.children || [];
      } else {
        branch = parentComp?.properties?.[branchKey] || [];
      }
      const oldIndex = branch.findIndex(sub => sub.id === activeId);

      let newIndex = -1;
      if (isOverBranchSortable && over.data.current.parentId === parentId && over.data.current.branchKey === branchKey) {
        newIndex = branch.findIndex(sub => sub.id === overId);
      } else if (isOverBranchDropZone && over.data.current.parentId === parentId && over.data.current.branchKey === branchKey) {
        const targetIndex = over.data.current.index;
        newIndex = targetIndex > oldIndex ? targetIndex - 1 : targetIndex;
      }

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        reorderBranchComponents(parentId, branchKey, oldIndex, newIndex);
      }
      setActiveId(null);
      setHoveredComponent(null);
      return;
    }

    if (isActiveFromPalette && (isOverCanvas || isOverDropZone || isOverSortableComponent)) {
      const componentType = active.data.current.componentType;
      const defaultProperties = active.data.current.defaultProperties;

      // BUG-002: Enforce per-screen component count limits at drop time.
      if (!canAddComponentToScreen(componentType, currentScreen)) {
        const message = getAddBlockerMessage(componentType, currentScreen);
        if (onDropBlocked) {
          onDropBlocked(message);
        }
        setActiveId(null);
        setHoveredComponent(null);
        return;
      }

      let insertIndex = currentScreen.components.length; // Default to end

      if (isOverSortableComponent) {
        const overComponent = over.data.current.component;
        const overIndex = currentScreen.components.findIndex(comp => comp.id === overComponent.id);
        // Insert after the component we're hovering over
        insertIndex = overIndex + 1;
      } else if (isOverDropZone) {
        insertIndex = over.data.current.index;
      }

      // Cap insertIndex so new components always land before the Footer
      var footerIdx = getFooterIndex(currentScreen);
      if (footerIdx !== -1) {
        insertIndex = Math.min(insertIndex, footerIdx);
      }

      addComponent(componentType, defaultProperties, insertIndex);

    } else if (!isActiveFromPalette && !isActiveBranchSortable && activeId !== overId) {
      const oldIndex = currentScreen.components.findIndex(item => item.id === activeId);
      let newIndex = -1;

      if (isOverSortableComponent) {
        const overComponent = over.data.current.component;
        newIndex = currentScreen.components.findIndex(item => item.id === overComponent.id);
      } else if (isOverDropZone) {
        // When dropping on a drop zone, use the index but adjust for the dragged item
        const targetIndex = over.data.current.index;
        newIndex = targetIndex > oldIndex ? targetIndex - 1 : targetIndex;
      }

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        reorderComponents(oldIndex, newIndex);
      }
    }

    setActiveId(null);
    setHoveredComponent(null);
  };

  const customCollisionDetection = (args) => {
    const { active, droppableContainers } = args;

    if (active && active.data.current?.type === 'branch-sortable') {
      const { parentId, branchKey } = active.data.current;
      const branchCollisions = rectIntersection({
        ...args,
        droppableContainers: Array.from(droppableContainers.values()).filter(c => {
          const d = c.data.current;
          return (d?.type === 'branch-sortable' || d?.type === 'branch-drop-zone') &&
            d.parentId === parentId && d.branchKey === branchKey;
        }),
      });
      if (branchCollisions.length > 0) return branchCollisions;
    }

    if (active && active.id.startsWith('palette-')) {
      return closestCorners(args);
    }

    if (active && !active.id.startsWith('palette-')) {
      const componentCollisions = rectIntersection({
        ...args,
        droppableContainers: Array.from(droppableContainers.values()).filter(
          container => container.data.current?.type === 'sortable-component'
        )
      });

      if (componentCollisions.length > 0) {
        return componentCollisions;
      }
    }

    return closestCorners(args);
  };

  return {
    activeId,
    hoveredComponent,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    customCollisionDetection,
  };
};

